import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar, Clock, ArrowRight, ArrowLeft, BookOpen, Loader2, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import SEO from "../components/SEO";

function fmtDate(iso, isAr) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(isAr ? "ar-KW" : "en-GB", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

export default function Blog() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get("c") || "";
  const [data, setData] = useState({ posts: null, categories: [] });

  useEffect(() => {
    api.get("/blog", { params: cat ? { category: cat } : {} })
      .then((r) => setData(r.data))
      .catch(() => setData({ posts: [], categories: [] }));
  }, [cat]);

  const t = useMemo(() => ({
    eyebrow: isAr ? "المدونة القانونية" : "Legal blog",
    title: isAr ? "أفكار وقراءات قانونية." : "Reading the Kuwaiti legal landscape.",
    lead: isAr
      ? "مقالات يكتبها فريق مجموعة الدخيل القانونية لتقريب الحلول القانونية لكل مهتم، بأسلوب واضح ومدعّم بالنصوص."
      : "Articles written by the Aldakheel Legal Group team to make Kuwaiti law accessible — practical, citation-backed, jargon-free.",
    all: isAr ? "كل التصنيفات" : "All categories",
    minRead: (n) => isAr ? `${n} دقيقة قراءة` : `${n} min read`,
    read: isAr ? "اقرأ المقال ←" : "Read the article →",
    empty: isAr ? "لا توجد مقالات في هذا التصنيف بعد." : "No articles in this category yet.",
  }), [isAr]);

  const posts = data.posts;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <SEO
        titleAr="المدونة القانونية — مجموعة الدخيل القانونية"
        titleEn="Legal Blog — Aldakheel Legal Group"
        descriptionAr={t.lead}
        descriptionEn={t.lead}
      />

      {/* HERO */}
      <div className="grid lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-7">
          <p className="eyebrow inline-flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" style={{ color: "var(--gold-deep)" }} />
            {t.eyebrow}
          </p>
          <h1 className="font-display text-5xl lg:text-6xl mt-3 leading-[1.05] tracking-tight">{t.title}</h1>
        </div>
        <div className="lg:col-span-5 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap items-center gap-2 mb-10">
        <button
          type="button"
          onClick={() => setSearchParams({}, { replace: true })}
          data-testid="blog-filter-all"
          className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border transition-colors ${
            !cat
              ? "bg-slate-900 text-white border-slate-900"
              : "border-slate-200 text-slate-600 hover:border-slate-900"
          }`}
        >
          {t.all}
        </button>
        {data.categories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setSearchParams({ c }, { replace: true })}
            data-testid={`blog-filter-${c}`}
            className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border transition-colors ${
              cat === c
                ? "bg-slate-900 text-white border-slate-900"
                : "border-slate-200 text-slate-600 hover:border-slate-900"
            }`}
          >
            {c}
          </button>
        ))}
        {cat && (
          <button
            type="button"
            onClick={() => setSearchParams({}, { replace: true })}
            data-testid="blog-filter-clear"
            className="inline-flex items-center gap-1 px-2 h-9 text-xs uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
          >
            <X className="w-3 h-3" />
            {isAr ? "مسح" : "Clear"}
          </button>
        )}
      </div>

      {/* POST GRID */}
      {posts === null ? (
        <div className="py-12 flex items-center justify-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : posts.length === 0 ? (
        <div className="py-16 text-center text-slate-500">{t.empty}</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="luxe-card group flex flex-col p-7 hover:border-slate-900 transition-colors"
              style={{ borderColor: "var(--line)" }}
            >
              <p className="eyebrow" style={{ color: "var(--gold-deep)" }}>
                {p.category}
              </p>
              <h3
                dir="auto"
                className="font-display text-xl lg:text-2xl mt-3 leading-snug group-hover:text-[var(--gold-deep)] transition-colors line-clamp-3"
              >
                {p.title}
              </h3>
              <p dir="auto" className="mt-4 text-sm text-slate-600 leading-relaxed line-clamp-3">
                {p.excerpt}
              </p>
              <div className="mt-6 pt-4 border-t flex items-center gap-4 text-xs text-slate-500"
                   style={{ borderColor: "var(--line)" }}>
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {fmtDate(p.published_at, isAr)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {t.minRead(p.read_minutes || 3)}
                </span>
              </div>
              <span className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-medium"
                    style={{ color: "var(--gold-deep)" }}>
                {t.read}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
